import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { useAuthorisation } from "../hooks/useAuthorisation";

function P2PListingDetailPage() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerAmount, setOfferAmount] = useState("");
  const [offerMessage, setOfferMessage] = useState("");
  const [isSubmittingOffer, setIsSubmittingOffer] = useState(false);
  const [offerError, setOfferError] = useState(null);
  const { getAccessTokenOrSignIn } = useAuthorisation();

  useEffect(() => {
    if (!listingId) {
      setError("No listing ID provided");
      setIsLoading(false);
      return;
    }

    const fetchListing = async () => {
      setIsLoading(true);
      setError(null);
      try {
        console.log(`Fetching listing ${listingId}...`);

        // Get authentication token
        const accessToken = await getAccessTokenOrSignIn();
        if (!accessToken) {
          throw new Error("Authentication required");
        }

        const apiBaseUrl =
          process.env.REACT_APP_API_BASE_URL || "";

        // Fetch listing details
        const response = await fetch(
          `${apiBaseUrl}/api/secure/p2p/listings/${listingId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Listing not found or no longer available");
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || "Failed to fetch listing");
        }

        console.log("Listing fetched successfully");
        setListing(data.listing);

        // Track view count
        try {
          await fetch(
            `${apiBaseUrl}/api/secure/p2p/listings/${listingId}/view`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          console.log("View count incremented");
        } catch (viewError) {
          console.error("Failed to increment view count:", viewError);
          // Don't fail the page load if view tracking fails
        }
      } catch (err) {
        console.error("Error fetching listing:", err);
        setError(err.message || "Failed to load listing");
      }
      setIsLoading(false);
    };

    fetchListing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);

  const handleBuyNow = () => {
    navigate(`/checkout/${listingId}`);
  };

  const handleMakeOffer = () => {
    setShowOfferModal(true);
    setOfferError(null);
    // Pre-fill with listing price as suggestion
    setOfferAmount(listing?.price ? listing.price.toString() : "");
  };

  const handleSubmitOffer = async (e) => {
    e.preventDefault();
    setIsSubmittingOffer(true);
    setOfferError(null);

    try {
      const amount = parseFloat(offerAmount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error("Please enter a valid offer amount");
      }

      // Get authentication token
      const accessToken = await getAccessTokenOrSignIn();
      if (!accessToken) {
        throw new Error("Authentication required");
      }

      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:8789";

      const response = await fetch(`${apiBaseUrl}/api/secure/p2p/offers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          listingId,
          offerAmount: amount,
          message: offerMessage || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Failed to submit offer");
      }

      // Close modal and show success
      setShowOfferModal(false);
      setOfferAmount("");
      setOfferMessage("");
      alert("Offer submitted successfully! The seller will be notified.");
    } catch (err) {
      console.error("Error submitting offer:", err);
      setOfferError(err.message || "Failed to submit offer");
    } finally {
      setIsSubmittingOffer(false);
    }
  };

  const handleCloseOfferModal = () => {
    setShowOfferModal(false);
    setOfferAmount("");
    setOfferMessage("");
    setOfferError(null);
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-8">
        <p>Loading listing...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-center">
          <p className="text-red-500 mb-4">Error: {error}</p>
          <Button onClick={() => navigate("/p2p/browse")}>
            Back to Browse
          </Button>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="p-4 md:p-8">
        <p>Listing not found</p>
      </div>
    );
  }

  return (
    <div className="pt-20 px-4 pb-4 md:pt-24 md:px-8 md:pb-8 max-w-6xl mx-auto">
      <Button
        variant="outline"
        onClick={() => navigate("/p2p/browse")}
        className="mb-4"
      >
        &larr; Back to Browse
      </Button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Left column - Images */}
        <div>
          {/* Main image */}
          <Card className="overflow-hidden mb-4">
            <img
              src={
                listing.images?.[selectedImageIndex] ||
                "https://via.placeholder.com/600"
              }
              alt={listing.title}
              className="w-full h-96 object-cover"
            />
          </Card>

          {/* Thumbnail strip */}
          {listing.images && listing.images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {listing.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded border-2 overflow-hidden ${
                    index === selectedImageIndex
                      ? "border-blue-500"
                      : "border-gray-300"
                  }`}
                >
                  <img
                    src={image}
                    alt={`${listing.title} thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right column - Details */}
        <div>
          <h1 className="text-3xl font-bold mb-2">{listing.title}</h1>

          {/* Metadata badges */}
          <div className="flex flex-wrap gap-2 mb-4">
            {listing.brand && (
              <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                {listing.brand}
              </span>
            )}
            {listing.category && (
              <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                {listing.category}
              </span>
            )}
            {listing.condition && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                {listing.condition}
              </span>
            )}
          </div>

          {/* Price */}
          <div className="mb-6">
            <p className="text-4xl font-bold text-gray-900">
              ${listing.price?.toFixed(2)}
            </p>
          </div>

          {/* Additional details */}
          {(listing.color || listing.material) && (
            <div className="mb-6 space-y-2">
              {listing.color && (
                <div className="flex">
                  <span className="text-gray-600 w-24">Color:</span>
                  <span className="font-medium">{listing.color}</span>
                </div>
              )}
              {listing.material && (
                <div className="flex">
                  <span className="text-gray-600 w-24">Material:</span>
                  <span className="font-medium">{listing.material}</span>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          {listing.description && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Description</h2>
              <p className="text-gray-700 whitespace-pre-wrap">
                {listing.description}
              </p>
            </div>
          )}

          {/* View count */}
          <div className="flex items-center gap-1 text-sm text-gray-500 mb-6">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            <span>{listing.viewCount || 0} views</span>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button onClick={handleBuyNow} size="lg" className="flex-1">
              Buy Now
            </Button>
            <Button
              onClick={handleMakeOffer}
              variant="outline"
              size="lg"
              className="flex-1"
            >
              Make Offer
            </Button>
          </div>

          {/* Listing metadata */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Listed on{" "}
              {listing.createdAt
                ? new Date(listing.createdAt * 1000).toLocaleDateString()
                : "Unknown"}
            </p>
          </div>
        </div>
      </div>

      {/* Offer Modal */}
      {showOfferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6 relative">
            <button
              onClick={handleCloseOfferModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              &times;
            </button>

            <h2 className="text-2xl font-bold mb-4">Make an Offer</h2>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Listing Price: ${listing?.price?.toFixed(2)}
              </p>
            </div>

            <form onSubmit={handleSubmitOffer}>
              <div className="mb-4">
                <label
                  htmlFor="offerAmount"
                  className="block text-sm font-medium mb-2"
                >
                  Your Offer Amount ($)
                </label>
                <input
                  type="number"
                  id="offerAmount"
                  step="0.01"
                  min="0.01"
                  value={offerAmount}
                  onChange={(e) => setOfferAmount(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your offer"
                />
              </div>

              <div className="mb-4">
                <label
                  htmlFor="offerMessage"
                  className="block text-sm font-medium mb-2"
                >
                  Message (Optional)
                </label>
                <textarea
                  id="offerMessage"
                  value={offerMessage}
                  onChange={(e) => setOfferMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Add a message to the seller..."
                />
              </div>

              {offerError && (
                <p className="text-red-500 text-sm mb-4">{offerError}</p>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseOfferModal}
                  disabled={isSubmittingOffer}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmittingOffer}
                  className="flex-1"
                >
                  {isSubmittingOffer ? "Submitting..." : "Submit Offer"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}

export default P2PListingDetailPage;
